import { defineStore } from 'pinia'
import { useSupabase } from './supabase'
import { useLocalStorage } from '@vueuse/core'
import { nanoid } from 'nanoid'
import dayjs from 'dayjs'
import { ref, computed, onMounted } from 'vue'
import { useVibrate } from '@/composables/useVibrate'

export type Photo = {
  photo_id: string
  user_id: string
  url: string
  description: string
  word: string
  word_id: number
  week_of: string
  username: string
  avatar: string
  likes: number
  liked: boolean
  bookmarked: boolean
  comments_count: number
  created_at: string
  updated_at: string
  trophy_type: 'FIRST' | 'SECOND' | 'THIRD' | null
  viewed: boolean
}

export const usePhotos = defineStore('photos', () => {
  const supabase = useSupabase()
  const { vibrate } = useVibrate()
  const words = ref<{ word: string; week_of: string }[]>([])
  const currentWord = ref<string>('') // week_of
  const prevWord = ref<string>('')
  const fetchingMore = ref(false)
  const fetchError = ref(false)
  const isInitialLoad = ref(true)
  const myPhotos = useLocalStorage<Photo[]>('myPhotos', [])
  const photos = ref<Photo[]>([])
  const sessionID = nanoid()

  const currentPhotos = computed(() => photos.value ?? [])

  function reset() {
    photos.value = []
    myPhotos.value = []
    words.value = []
    currentWord.value = ''
    prevWord.value = ''
    fetchError.value = false
    isInitialLoad.value = true
  }

  async function like(photo_id: string) {
    if (supabase.user.status !== 'SignedIn') return false
    vibrate(5)

    const loadedPhoto = photos.value?.find((photo) => photo.photo_id === photo_id)

    if (loadedPhoto) {
      if (loadedPhoto.liked) {
        loadedPhoto.likes--
        loadedPhoto.liked = false
      } else {
        loadedPhoto.likes++
        loadedPhoto.liked = true
      }
    }

    const { data: photo, error: photoError } = await supabase.api
      .from('album')
      .select('liked, week_of, word')
      .eq('photo_id', photo_id)
      .single()

    if (!photo) return false

    if (photo.liked) {
      const { error } = await supabase.api
        .from('likes')
        .delete()
        .eq('photo_id', photo_id)
        .eq('user_id', supabase.user.data.id)

      if (error) {
        console.error(error)
        return false
      }
    } else {
      const { error } = await supabase.api.from('likes').insert({
        photo_id,
        user_id: supabase.user.data.id
      })

      if (error) {
        console.error(error)
        return false
      }
    }

    return true
  }

  async function bookmark(photo_id: string) {
    if (supabase.user.status !== 'SignedIn') return
    vibrate(5)

    const loadedPhoto = photos.value.find((photo) => photo.photo_id === photo_id)

    if (loadedPhoto) {
      if (loadedPhoto.bookmarked) {
        loadedPhoto.bookmarked = false
      } else {
        loadedPhoto.bookmarked = true
      }
    }

    const { data: photo, error: photoError } = await supabase.api
      .from('album')
      .select('bookmarked, week_of')
      .eq('photo_id', photo_id)
      .single()

    if (!photo) return

    if (photo.bookmarked) {
      const { error } = await supabase.api
        .from('bookmarks')
        .delete()
        .eq('photo_id', photo_id)
        .eq('user_id', supabase.user.data.id)

      if (error) {
        console.error(error)
        return
      }
    } else {
      const { error } = await supabase.api.from('bookmarks').insert({
        photo_id,
        user_id: supabase.user.data.id
      })

      if (error) {
        console.error(error)
        return
      }
    }
  }

  async function fetchMyPhotos() {
    if (supabase.user.status !== 'SignedIn') return
    const { data, error } = await supabase.api
      .from('album')
      .select('*')
      .eq('user_id', supabase.user.data.id)
      .order('created_at', { ascending: false })

    myPhotos.value = data ?? []
  }

  async function fetchTimelinePhotos() {
    if (!fetchingMore.value) {
      fetchingMore.value = true

      const loadedPhotos = photos.value

      const { data, error } = await supabase.api
        .from('album')
        .select('*')
        .not('photo_id', 'in', `(${loadedPhotos.map((photo) => photo.photo_id).join(',')})`)
        .limit(10)

      if (error) {
        console.error('Failed to fetch timeline photos:', error)
        fetchError.value = true
      } else {
        photos.value = [...loadedPhotos, ...data]
        fetchError.value = false
      }

      fetchingMore.value = false
      isInitialLoad.value = false
    }
  }

  async function markPhotoViewed(index: number) {
    if (supabase.user.status !== 'SignedIn') return

    const loadedPhoto = photos.value[index]

    if (loadedPhoto?.viewed) return

    if (loadedPhoto) {
      loadedPhoto.viewed = true
    }

    const { error } = await supabase.api.from('viewed_photos').insert({
      photo_id: loadedPhoto.photo_id,
      user_id: supabase.user.data.id
    })
  }

  onMounted(async () => {
    if (currentWord.value) return

    const { data: wordsData, error: wordsError } = await supabase.api
      .from('words')
      .select('word,week_of')
      .lte('week_of', dayjs().subtract(1, 'week').toISOString())
      .order('week_of', { ascending: false })
      .limit(1)

    if (wordsError) {
      console.error(wordsError)
    } else {
      currentWord.value = wordsData[0].week_of
      await fetchTimelinePhotos()
    }
  })

  async function retryFetchTimelinePhotos() {
    fetchError.value = false
    isInitialLoad.value = true
    await fetchTimelinePhotos()
  }

  return {
    words,
    currentWord,
    fetchingMore,
    fetchError,
    isInitialLoad,
    currentPhotos,
    myPhotos,
    sessionID,
    like,
    bookmark,
    fetchMyPhotos,
    fetchTimelinePhotos,
    retryFetchTimelinePhotos,
    markPhotoViewed,
    reset
  }
})
